import { StorageSerializers, useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed } from 'vue';
import type {
  AuthData,
  LoginCompany,
  LoginUser,
  WelfarePlan,
} from './auth-types.ts';

export const AUTH_STORE_NAME = 'auth';

export const useAuthStore = defineStore(AUTH_STORE_NAME, () => {
  const authData = useStorage<AuthData | null>(
    AUTH_STORE_NAME,
    null,
    localStorage,
    { serializer: StorageSerializers.object },
  );
  const selectedCompany = useStorage<LoginCompany | null>(
    'selectedCompany',
    null,
    localStorage,
    { serializer: StorageSerializers.object },
  );

  const setCompanySelected = (company: LoginCompany | null) => {
    if (company === null) {
      selectedCompany.value = null;
      return;
    }

    selectedCompany.value = {
      ...(selectedCompany.value ?? {}),
      ...company,
    } as LoginCompany;
  };

  // Replaces instead of merging (unlike setCompanySelected) so detail fields
  // left over from a previously selected company cannot survive hydration.
  const hydrateCompanyDetail = (company: LoginCompany) => {
    selectedCompany.value = company;
  };

  const setAuth = (userData: AuthData) => {
    authData.value = {
      ...userData,
      companies: [...(userData.companies ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    };
  };

  const logout = () => {
    authData.value = null;
    setCompanySelected(null);
  };

  const isLogged = computed<boolean>(() => {
    if (authData.value?.companies && authData.value?.companies.length > 0) {
      return !!selectedCompany.value;
    }
    return !!authData.value?.user;
  });

  const user = computed<LoginUser | undefined>(() => {
    return authData.value?.user;
  });

  const userCompanies = computed<LoginCompany[] | undefined>(() => {
    return authData.value?.companies.sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  });

  const currentWelfarePlan = computed<WelfarePlan | undefined>(() => {
    return selectedCompany.value?.currentWelfarePlan;
  });

  const hasMealVoucher = computed<boolean>(() => {
    return selectedCompany.value?.hasMealVoucher ?? false;
  });

  return {
    isLogged,
    user,
    authData,
    selectedCompany,
    setCompanySelected,
    hydrateCompanyDetail,
    setAuth,
    logout,
    userCompanies,
    currentWelfarePlan,
    hasMealVoucher,
  };
});
